import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { getPostsPaginated } from "../lib/sanity/client.js";
import { Link } from "react-router-dom";
import { urlFor } from "../lib/sanity/image.js";
import "./Blogs.css";

function formatDate(dateString) {
  const date = new Date(dateString);
  const options = { year: "numeric", month: "long", day: "numeric" };
  return date.toLocaleDateString("en-US", options);
}

// Loading skeleton component
const BlogCardSkeleton = () => (
  <div className="blogs-card skeleton">
    <div className="skeleton-image"></div>
    <div className="skeleton-content">
      <div className="skeleton-date"></div>
      <div className="skeleton-title"></div>
      <div className="skeleton-title-short"></div>
    </div>
  </div>
);

// Error component
const ErrorState = ({ onRetry }) => (
  <div className="error-container">
    <div className="error-content">
      <svg
        className="error-icon"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
        />
      </svg>
      <h3 className="error-title">Something went wrong</h3>
      <p className="error-message">
        We couldn't load the blog posts. Please check your connection and try
        again.
      </p>
      <button onClick={onRetry} className="error-retry-btn">
        <svg
          className="retry-icon"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          />
        </svg>
        Try Again
      </button>
    </div>
  </div>
);

// Empty state component
const EmptyState = () => (
  <div className="empty-container">
    <div className="empty-content">
      <svg
        className="empty-icon"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
      <h3 className="empty-title">No blog posts yet</h3>
      <p className="empty-message">Check back later for new content!</p>
    </div>
  </div>
);

const Blogs = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 12;

  const { data, isLoading, error, isFetching, refetch } = useQuery({
    queryKey: ["posts", currentPage],
    queryFn: () => getPostsPaginated(currentPage, postsPerPage),
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });

  // Initial loading state with skeletons
  if (isLoading) {
    return (
      <div className="blogs-container">
        <h2 className="blogs-header">Our Blog</h2>
        <div className="blogs-grid">
          {Array.from({ length: postsPerPage }, (_, index) => (
            <BlogCardSkeleton key={index} />
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="blogs-container">
        <h2 className="blogs-header">Our Blog</h2>
        <ErrorState onRetry={refetch} />
      </div>
    );
  }

  const { posts, total, totalPages, hasNext, hasPrev } = data;

  // Empty state
  if (!posts || posts.length === 0) {
    return (
      <div className="blogs-container">
        <h2 className="blogs-header">Our Blog</h2>
        <EmptyState />
      </div>
    );
  }

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 5; i++) {
          pageNumbers.push(i);
        }
      } else if (currentPage >= totalPages - 2) {
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        for (let i = currentPage - 2; i <= currentPage + 2; i++) {
          pageNumbers.push(i);
        }
      }
    }
    return pageNumbers;
  };

  // Calculate jump pages logic
  const jumpPages = {
    canJumpBackward: currentPage > 5,
    canJumpForward: currentPage < totalPages - 4,
    jumpBackwardPage: Math.max(1, currentPage - 10),
    jumpForwardPage: Math.min(totalPages, currentPage + 10),
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Calculate display info
  const startIndex = (currentPage - 1) * postsPerPage;
  const endIndex = Math.min(startIndex + posts.length, total);

  return (
    <div className="blogs-container">
      <h2 className="blogs-header">Our Blog</h2>

      <div className={`blogs-grid ${isFetching ? "loading" : ""}`}>
        {posts.map((post) => (
          <div key={post._id} className="blogs-card">
            <Link to={`/blogs/${post?.slug?.current}`} className="blogs-link">
              {post?.mainImage && (
                <img
                  src={urlFor(post.mainImage)
                    .width(800)
                    .height(400)
                    .quality(80)
                    .auto("format")
                    .url()}
                  alt={post.mainImage.alt || post.title}
                  className="blogs-image"
                  loading="lazy"
                />
              )}
              <p className="blogs-date">{formatDate(post.publishedAt)}</p>
              <h2 className="blogs-title">{post.title}</h2>
            </Link>
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={!hasPrev || isFetching}
            className="pagination-btn pagination-prev"
          >
            Previous
          </button>

          {/* Jump backward button */}
          {jumpPages.canJumpBackward && (
            <>
              <button
                onClick={() => handlePageChange(1)}
                disabled={isFetching}
                className="pagination-btn"
              >
                1
              </button>
              <span className="pagination-ellipsis">...</span>
              <button
                onClick={() => handlePageChange(jumpPages.jumpBackwardPage)}
                disabled={isFetching}
                className="pagination-btn pagination-jump"
                title={`Jump to page ${jumpPages.jumpBackwardPage}`}
              >
                «
              </button>
            </>
          )}

          {/* Page numbers */}
          {getPageNumbers().map((page) => (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              disabled={isFetching}
              className={`pagination-btn ${
                currentPage === page ? "pagination-active" : ""
              }`}
            >
              {page}
            </button>
          ))}

          {/* Jump forward button */}
          {jumpPages.canJumpForward && (
            <>
              <button
                onClick={() => handlePageChange(jumpPages.jumpForwardPage)}
                disabled={isFetching}
                className="pagination-btn pagination-jump"
                title={`Jump to page ${jumpPages.jumpForwardPage}`}
              >
                »
              </button>
              <span className="pagination-ellipsis">...</span>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={isFetching}
                className="pagination-btn"
              >
                {totalPages}
              </button>
            </>
          )}

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={!hasNext || isFetching}
            className="pagination-btn pagination-next"
          >
            Next
          </button>
        </div>
      )}

      <div className="posts-info">
        Showing {startIndex + 1}-{endIndex} of {total} posts
      </div>
    </div>
  );
};

export default Blogs;
